import { prefersReducedMotion, useReveal } from "../lib/useReveal";

interface Layer {
  name: string;
  copy: string;
  color: string;
}

const LAYERS: Layer[] = [
  { name: "Edge / CDN", copy: "Caching, routing, and protection at Cloudflare's edge, before traffic reaches an origin.", color: "#67d3ff" },
  { name: "Application", copy: "Stateless server-rendered pages and APIs that scale out without sticky session state.", color: "#4d97ff" },
  { name: "Services", copy: "Business logic, integrations, and async work kept behind clear, testable boundaries.", color: "#3a82ff" },
  { name: "Data", copy: "Durable storage with isolation, backups, and the smallest necessary access surface.", color: "#2d6cf6" },
];

const SLAB_X = 70;
const SLAB_W = 280;
const SLAB_H = 46;
const GAP = 70;
const TOP = 34;

export function ArchitectureLayers(): React.ReactElement {
  const { ref, shown } = useReveal(0.3);
  const reduced = prefersReducedMotion();
  const spineTop = TOP;
  const spineBottom = TOP + (LAYERS.length - 1) * GAP + SLAB_H;

  return (
    <div ref={ref} className={`viz viz-arch${shown ? " is-shown" : ""}`}>
      <div className="arch-stage">
        <svg viewBox="0 0 420 320" role="img" aria-label="Request flow through edge, application, services and data layers.">
          <defs>
            <linearGradient id="arch-slab" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#13284f" />
              <stop offset="1" stopColor="#0c1c3c" />
            </linearGradient>
          </defs>

          <path id="arch-spine" d={`M 210 ${spineTop} L 210 ${spineBottom}`} className="arch-spine" />
          {shown &&
            !reduced &&
            [0, 1, 2].map((dot) => (
              <circle key={dot} r="3.4" className="arch-pulse">
                <animateMotion
                  dur="2.6s"
                  begin={`${dot * 0.7}s`}
                  repeatCount="indefinite"
                  keyPoints="0;1"
                  keyTimes="0;1"
                >
                  <mpath href="#arch-spine" />
                </animateMotion>
              </circle>
            ))}

          {LAYERS.map((layer, index) => {
            const y = TOP + index * GAP;
            return (
              <g
                key={layer.name}
                className="arch-slab"
                style={{ transitionDelay: `${index * 130}ms` }}
              >
                <rect x={SLAB_X} y={y + 8} width={SLAB_W} height={SLAB_H} rx="12" className="arch-slab-base" />
                <rect x={SLAB_X} y={y} width={SLAB_W} height={SLAB_H} rx="12" fill="url(#arch-slab)" className="arch-slab-face" />
                <rect x={SLAB_X} y={y} width="4" height={SLAB_H} rx="2" fill={layer.color} />
                <text x={SLAB_X + 22} y={y + SLAB_H / 2 + 5} className="arch-slab-label">
                  {layer.name}
                </text>
                <circle cx={SLAB_X + SLAB_W - 22} cy={y + SLAB_H / 2} r="4" fill={layer.color} />
              </g>
            );
          })}
        </svg>
      </div>
      <ol className="arch-legend">
        {LAYERS.map((layer) => (
          <li key={layer.name} style={{ "--dot": layer.color } as React.CSSProperties}>
            <span className="arch-dot" aria-hidden="true" />
            <div>
              <strong>{layer.name}</strong>
              <p>{layer.copy}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
